import React, { useCallback, useEffect, useState } from 'react'
import { Bold, MqDesktop, MqTablet } from 'streamr-ui/css/consts'
import styled from 'styled-components'
import { openNewsletterConfirmation } from '~utils/index'
import Display from '~shared/Display'
import { NewsletterCaptcha as PrestyledNewsletterCaptcha } from './NewsletterCaptcha'
import { NewsletterEmailAgreement } from './NewsletterEmailAgreement'

export function NewsletterForm() {
    const [email, setEmail] = useState('')

    const [isSubmitting, setIsSubmitting] = useState(false)

    const [form, setForm] = useState(null)

    const [showExtras, setShowExtras] = useState(false)

    const [captchaToken, setCaptchaToken] = useState()

    const [agreedToReceive, setAgreedToReceive] = useState(false)

    const canSubmit = !!email && !!captchaToken && !isSubmitting && agreedToReceive

    const submit = useNewsletterFormSubmitCallback()

    return (
        <NewsletterFormRoot
            ref={setForm}
            onSubmit={async (e) => {
                e.preventDefault()

                if (!canSubmit) {
                    return
                }

                setIsSubmitting(true)

                try {
                    await submit(form)

                    setEmail('')

                    setAgreedToReceive(false)

                    setCaptchaToken(null)

                    openNewsletterConfirmation()
                } catch (e) {
                    console.warn('Failed to submit the newsletter form', e)
                } finally {
                    setIsSubmitting(false)

                    setShowExtras(false)
                }
            }}
        >
            <Title>
                <Display as="span" mobile="none" tablet="inline">
                    Subscribe to the Streamr newsletter
                </Display>
                <Display as="span" tablet="none">
                    Subscribe to the newsletter
                </Display>
            </Title>
            <Fields>
                <EmailInput
                    readOnly={isSubmitting}
                    aria-label="email"
                    aria-required="true"
                    type="email"
                    data-inputmask=""
                    name="fields[email]"
                    placeholder="Email"
                    autoComplete="email"
                    onClick={() => {
                        setShowExtras(true)
                    }}
                    onBlur={() => {
                        if (!email) {
                            setShowExtras(false)
                        }
                    }}
                    onChange={(e) => {
                        setEmail(e.target.value)
                    }}
                    value={email}
                />
                <SubmitButton type="submit" disabled={!canSubmit}>
                    {isSubmitting ? 'Submitting…' : 'Subscribe'}
                </SubmitButton>
            </Fields>
            {showExtras && (
                <>
                    <NewsletterEmailAgreement
                        checked={agreedToReceive}
                        toggle={() => {
                            setAgreedToReceive((c) => !c)
                        }}
                    />
                    <NewsletterCaptcha onTokenChange={setCaptchaToken} />
                </>
            )}
            <input type="hidden" name="ml-submit" value="1" />
            <input type="hidden" name="anticsrf" value="true" />
        </NewsletterFormRoot>
    )
}

const NewsletterCaptcha = styled(PrestyledNewsletterCaptcha)`
    margin-top: 1rem;
`

const Title = styled.div`
    font-weight: ${Bold};
    letter-spacing: 0.0625rem;
    margin-bottom: 1.5rem;
    text-transform: uppercase;
`

const EmailInput = styled.input`
    appearance: none;
    border: 0.0625rem solid #efefef;
    border-radius: 0.25rem;
    box-sizing: border-box;
    color: #323232;
    outline: 0;
    padding: 0 1rem;
    transition: 200ms border-color;
    width: 100%;

    &:focus {
        border-color: #0324ff;
    }

    &::placeholder {
        color: #cdcdcd;
    }
`

const SubmitButton = styled.button`
    appearance: none;
    background-color: #efefef;
    border: 0;
    border-radius: 0.25rem;
    color: #323232;
    font-weight: 500;
    height: 3.5rem;
    outline: 0;
    padding: 0;
    transform: translateZ(0) scale(1);
    transition: 200ms;
    transition-property: background-color, transform;
    width: 100%;

    &:hover,
    &:focus {
        background-color: #e7e7e7;
        transform: translateZ(0) scale(1.05);
    }

    &:active {
        background-color: #d8d8d8;
        transform: translateZ(0) scale(0.95);
        transition-duration: 10ms;
        transition-timing-function: ease-out;
    }

    &:disabled {
        background-color: #efefef;
        color: #aaa;
        transform: translateZ(0);
    }
`

const Fields = styled.div`
    @media ${MqDesktop} {
        display: flex;
        gap: 1rem;
    }
`

const NewsletterFormRoot = styled.form`
    padding: 1rem 0 3.5rem;

    ${EmailInput},
    ${SubmitButton} {
        height: 3.5rem;
    }

    @media ${MqTablet} {
        padding: 0.5rem 0 2rem;
    }

    @media ${MqDesktop} {
        font-size: 0.875rem;
        padding: 4rem 0 5rem;

        ${SubmitButton} {
            width: 9rem;
        }

        ${EmailInput} {
            width: 19rem;
        }

        ${EmailInput},
        ${SubmitButton} {
            height: 2.5rem;
        }
    }
`

export function useMailerLiteAnalyticsEffect() {
    useEffect(() => {
        ;(async () => {
            try {
                await fetch(
                    'https://assets.mailerlite.com/jsonp/1359367/forms/148043583523390785/takel',
                )
            } catch (_) {
                // Ignore
            }
        })()
    }, [])
}

export function useNewsletterFormSubmitCallback() {
    const [abortController, setAbortController] = useState()

    useEffect(
        function abortCurrentControllerOnChangeOrUnmount() {
            return () => {
                if (abortController) {
                    abortController.abort()
                }
            }
        },
        [abortController],
    )

    return useCallback(async (form) => {
        if (!form) {
            return
        }

        const newAbortController = new AbortController()

        const { signal } = newAbortController

        setAbortController(newAbortController)

        const response = await fetch(
            'https://assets.mailerlite.com/jsonp/1359367/forms/148043583523390785/subscribe',
            {
                method: 'POST',
                body: new FormData(form),
                signal,
            },
        )

        const body = await response.json()

        if (signal.aborted) {
            throw new DOMException('Aborted', 'AbortError')
        }

        if (!body.success) {
            throw new Error(`Failed to submit form: ${JSON.stringify(body)}`)
        }
    }, [])
}
